import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState,getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import MiniCart from "@MasterBluePrint/Components/MiniCart";
import ShoppingCart from "@MasterBluePrint/Icons/ShoppingCart";
import { getShowMiniCartSetting } from "@MasterBluePrint/Store/Context/ContextSelector";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    return{
        cart,
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        totalQtyOrdered: getCurrentCartState(state).value?.totalQtyOrdered,
        showMiniCart: getShowMiniCartSetting(state)?.showMiniCart,
        currentLocation: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    overlay: InjectableCss;
    parentWrapper: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 3.125rem;
            padding: 0 .250625rem 0 .625rem;
        `,
    },
    overlay: {
        css: css`
            display: none;
            position: absolute;
            z-index: 2;
            opacity: 0.32;
            background-color: rgb(0, 37, 84);
            top: 108px;
            left: 0;
            width: 100%;
            height: calc(100% - 108px);

            @media (max-width: 360px) {
                left: 0;
                right: 0;
                width: 380px;
            }
        `,
    },
    parentWrapper: {
        css: css`
            > div:not(.custom-overlay) {
                position: relative;
            }
            &.minicart-active {
                .custom-overlay {
                    display: block;
                }
            }
        `,
    }
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    cart,
    totalQtyOrdered,
    showMiniCart,
    currentLocation
}) => {
    const currentUrl = currentLocation.pathname;
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false,getCartLines: true });
        }
    }, [shouldLoadCart]);
    
    useEffect(() => {
        if (isMounted && totalQtyOrdered && totalQtyOrdered > prevTotalQtyOrdered && cartUrl !== currentUrl) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false,getCartLines: true });
            setisOpen(true);

            if (typeof document !== "undefined") {
                const quickBuy = document.querySelector('button[data-test-selector="popoverOverflowTrigger"]')as HTMLElement;
            
                if (quickBuy.classList.contains('modal-open')) {
                    quickBuy.classList.remove('modal-open');
                    quickBuy.click();
                }
            }
        }
        if (!isMounted && totalQtyOrdered !== undefined) {
            setIsMounted(true);
        }
        if (totalQtyOrdered !== undefined) {
            setPrevTotalQtyOrdered(totalQtyOrdered);
        }
       
    }, [totalQtyOrdered]);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;
    const [isOpen, setisOpen] = React.useState(false);
    const [isMounted, setIsMounted] = React.useState(false);
    const [prevTotalQtyOrdered, setPrevTotalQtyOrdered] = React.useState(0);
    const closeMiniCart = () => {
        setisOpen(false);
    };
    return (
        <Typography as="div" className={`${isOpen ? "minicart-active" : ""}`} {...styles.parentWrapper}>
            <Typography as="div">
                <Link
                    {...routerLink}
                    href={cartUrl}
                    icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                    data-test-selector="cartLink"
                    onClick={()=>closeMiniCart()}
                >
                    <Hidden below="lg" as="span">
                        {showLabel && `${translate("Cart")} `}
                        <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
                            {retainedTotalCountToDisplay}
                        </Typography>
                    </Hidden>
                    <Hidden above="md" as="span">
                        <Typography as="span" {...styles.mobileCountText}>
                            {retainedTotalCountToDisplay}
                        </Typography>
                    </Hidden>
                    <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                </Link>
                {showMiniCart &&
                    <MiniCart isOpen={isOpen} closeMiniCart={closeMiniCart} />
                }
            </Typography>
            {showMiniCart &&
                <Typography as="div" className="custom-overlay" {...styles.overlay}></Typography>
            }
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLink),
    definition: {
        displayName: "Cart",
        icon: "Link",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;